// Curriculum data for the FDE AI Industry Practitioner Program.
// Content sourced from the program brief.

export const PROGRAM_META = {
  name: "Forward Deployed Engineering",
  subtitle: "AI Industry Practitioner Program",
  tagline: "Deploy first. Understand deeply.",
  duration: "7 – 8 months",
  effort: "~600+ hours of hands-on work",
  cohortNote: "Instructor-led · Live labs · Industry sprints · Capstone",
  certificate: "FDE AI Industry Practitioner",
};

export const PHASES = [
  {
    id: "phase-1",
    number: "01",
    title: "Python · Data · Statistics",
    weeks: "4 Weeks",
    focus: "Foundational skills every AI practitioner ships with.",
    modules: [
      "Python for AI: Anaconda, VS Code, NumPy, Pandas, Plotly, REST APIs",
      "Data wrangling & feature engineering — Streamlit EDA dashboards",
      "Statistics for AI: probability, Bayes, hypothesis tests, A/B testing",
    ],
    project: {
      industry: "BPO",
      name: "Operations Analytics Dashboard",
      stack: "Python · Pandas · Plotly · Streamlit Cloud",
    },
  },
  {
    id: "phase-2",
    number: "02",
    title: "Machine Learning · Deep Learning",
    weeks: "6 Weeks",
    focus: "Train and evaluate models that survive production.",
    modules: [
      "Classical ML: regression, classification, ensembles, PCA",
      "Deep learning: CNNs, RNNs/LSTMs, TensorFlow, PyTorch",
      "MLOps fundamentals: MLflow, DVC, FastAPI, Docker",
    ],
    project: {
      industry: "BFSI",
      name: "Real-Time Fraud Detection API",
      stack: "XGBoost · FastAPI · Docker · GCP Cloud Run",
    },
  },
  {
    id: "phase-3",
    number: "03",
    title: "NLP · Speech AI · TTS / STT",
    weeks: "5 Weeks",
    focus: "Voice and language capabilities that meet enterprise SLAs.",
    modules: [
      "NLP foundations: TF-IDF, NER, sentiment, semantic search",
      "Transformers: BERT, RoBERTa, Hugging Face pipelines",
      "Speech AI: Whisper fine-tuning, Coqui / Azure Neural TTS",
      "Deploying to client CRMs with GDPR-safe pipelines",
    ],
    project: {
      industry: "BPO",
      name: "Voice AI: STT + NLP + Auto-Ticket Creation",
      stack: "Whisper · Azure Speech · Hugging Face · Salesforce API",
    },
  },
  {
    id: "phase-4",
    number: "04",
    title: "Generative AI · LLMs · RAG",
    weeks: "5 Weeks",
    focus: "Ship LLM applications with grounded, retrievable knowledge.",
    modules: [
      "LLM landscape: Claude, GPT, Gemini — APIs, tool use, cost control",
      "Prompt engineering & structured outputs",
      "RAG systems: embeddings, vector DBs, LangChain / LlamaIndex",
      "Fine-tuning LLMs on client data — SFT & PEFT",
    ],
    project: {
      industry: "Retail",
      name: "AI Shopping Assistant with Product RAG",
      stack: "Claude API · Pinecone · LangChain · Azure Container Apps",
    },
  },
  {
    id: "phase-5",
    number: "05",
    title: "Multi-Agent Systems",
    weeks: "5 Weeks",
    focus: "Design agent hierarchies that stay observable in production.",
    modules: [
      "Agent fundamentals: ReAct, tools, memory",
      "Orchestration: LangGraph, CrewAI, AutoGen, Semantic Kernel",
      "Hierarchical agents with human-in-the-loop and guardrails",
      "Observability: tracing, drift, hallucination detection",
    ],
    project: {
      industry: "Healthcare",
      name: "Multi-Level Triage Agent System",
      stack: "LangGraph · Claude · CrewAI · Redis · GCP · LangSmith",
    },
  },
  {
    id: "phase-6",
    number: "06",
    title: "Cloud Deployment · Monitoring · Capstone",
    weeks: "5 Weeks",
    focus: "Ship, monitor, and iterate on production AI systems.",
    modules: [
      "Building tailored models for clients: privacy, compression, model cards",
      "GCP stack: Vertex AI, Cloud Run, BigQuery ML, Pub/Sub + Dataflow",
      "Azure stack: AI Foundry, Azure ML, Azure OpenAI, AKS, Cognitive Services",
      "Production monitoring: drift, LLM-specific signals, retraining triggers",
      "CI/CD for AI: GitHub Actions, Azure DevOps, blue-green & canary",
    ],
    project: {
      industry: "Capstone",
      name: "End-to-End Production AI System",
      stack: "GCP or Azure · Full monitoring · CI/CD pipelines",
    },
  },
];

export const INDUSTRIES = [
  { name: "BPO", detail: "Contact-centre analytics, voice AI, auto-ticketing" },
  { name: "BFSI", detail: "Fraud detection, risk scoring, KYC copilots" },
  { name: "Healthcare", detail: "Triage agents, clinical decision support" },
  { name: "Retail", detail: "Shopping assistants, RAG catalogues" },
  { name: "Manufacturing", detail: "Predictive maintenance, vision QA" },
  { name: "SaaS", detail: "Customer-facing copilots, embedded LLMs" },
];

export const CAREER_ROLES = [
  "Forward Deployed Engineer",
  "Generative AI Engineer",
  "MLOps Engineer",
  "AI Solutions Architect",
  "LLM Applications Engineer",
];

export const ASSESSMENT = [
  { label: "Weekly labs", pct: 20 },
  { label: "Phase projects", pct: 40 },
  { label: "Capstone", pct: 30 },
  { label: "Peer code review", pct: 10 },
];
